<template>
  <SectionHeader
    :show-content="bidAdjustmentsShowContents"
    :widget-title="$t('HEADER_BID_ADJUSTMENTS')"
    @onShowContentsClick="onBidAdjustmentsShowContentsClick()"
  >
    <template #content>
      <div v-if="overridesAvailable">
        <Field
          :label="$t('LABEL_OVERRIDE_BID_ADJUSTMENTS')"
          :hide-label="false"
          :has-tooltip="true"
          :tooltip-text="$t('TOOLTIP_OVERRIDE_BID_ADJUSTMENTS')"
        >
          <template #element>
            <div class="p-l-0 col-sm-5 col-md-1">
              <ValidationProvider
                rules="required"
                slim
              >
                <nzToggle
                  v-model="overrideBidAdjustments"
                  @onToggle="overrideBidAdjustmentsToggle"
                />
              </ValidationProvider>
            </div>
          </template>
        </Field>
      </div>
      <div v-if="!overridesAvailable || overrideBidAdjustments">
        <DraggableTable
          v-model="rows"
          :current-row="currentRow"
          :last-row-id="lastRowId"
          :edit-row="editRow"
          :headers="headers"
          :adjustment-panel-properties="adjustmentPanelProperties"
          :show-add-adjustment-button="showAddAdjustmentButton"
          :adjustments-changed="adjustmentsChanged"
          @onShowRowAdjustment="onShowRowAdjustment"
          @onAddAdjustment="onAddAdjustment"
          @onEditAdjustment="onEditAdjustment"
          @onAddAdjustmentButtonClick="onAddAdjustmentButtonClick"
          @onResetAdjustments="onResetAdjustments"
          @onSaveAdjustments="onSaveAdjustments"
          @onCancelAdjustments="onCancelAdjustments"
          @onOrderChange="onOrderChange"
          @drag="onDrag"
          @onSetCurrentRow="onSetCurrentRow"
          @onToggleEditRow="onToggleEditRow"
          @onRemoveRow="onRemoveRow"
        >
          <template #sanitizedRow="{ row }">
            <div class="draggable__dragbutton">
              <span
                class="draggable__dragbutton-action"
                :class="{'draggable-table-cursor': !showAddAdjustmentButton}"
              >
                <i
                  class="glyphicon glyphicon-option-vertical"
                  aria-hidden="true"
                />
                <i
                  class="glyphicon glyphicon-option-vertical"
                  aria-hidden="true"
                />
              </span>
            </div>
            <span
              v-for="(val, propertyName) in sanitizeRow(row)"
              :key="propertyName"
              :class="'bid-adjustment-settings-summary__' + propertyName.toLowerCase()"
            >
              {{ val }}
            </span>
          </template>
          <template #actions="{ row, showRowAdjustment }">
            <div class="bid-adjustment-settings-summary__actions">
              <div class="actions-collapsed-list">
                <a
                  href="javascript:;"
                  :disabled="showAddAdjustmentButton"
                  class="btn btn-list-function action-icon"
                  @click="!showAddAdjustmentButton ? showRowAdjustment(row) : null"
                >
                  <i
                    v-tooltip.bottom.hover="{text:$t('EDIT_BID_MODIFIER'), transition: 0}"
                    class="typcn typcn-edit"
                    aria-hidden="true"
                  />
                  <span
                    v-t="'EDIT_BID_MODIFIER'"
                    class="hint-text-mobile"
                  />
                </a>

                <a
                  href="javascript:;"
                  :disabled="showAddAdjustmentButton"
                  class="btn btn-list-function action-icon"
                  @mousedown="!showAddAdjustmentButton ? onRemoveRow(row) : null"
                >
                  <i
                    v-tooltip.bottom.hover="{text:$t('DELETE'), transition: 0}"
                    class="typcn typcn-trash"
                    aria-hidden="true"
                  />
                  <span
                    v-t="'DELETE'"
                    class="hint-text-mobile"
                  />
                </a>
              </div>
            </div>
          </template>
        </DraggableTable>
      </div>
    </template>
  </SectionHeader>
</template>

<script>
import messages from '../locales';
import sharedMessages from '@/core/locales';
import { tagIdToText } from '@/core/filters/tagIdToText';
import { availableTags } from '@/data/filterData';

import SectionHeader from '@/core/components/SectionHeader/SectionHeader';
import Field from '@/core/components/LabelAndElement/LabelAndElement';
import DraggableTable from '@/core/components/DraggableTable/DraggableTable';
import nzToggle from '@/core/components/nzToggle/nzToggle';

const CURRENCY_DECIMAL_DIGITS = 2;
const MULTIPLIER_DECIMAL_DIGITS = 3;

export default {
  components: { SectionHeader, Field, DraggableTable, nzToggle },
  inheritAttrs: false,
  model:{
    value: 'rows',
    event: 'onOrderChange'
  },
  props: {
    rows: {
      type: Array,
      default() {
        return [];
      }
    },
    currentRow: {
      type: Object,
      default() {
        return {};
      }
    },
    lastRowId: {
      type: String,
      default() {
        return '';
      }
    },
    editRow: {
      type: Boolean,
      default() {
        return false;
      }
    },
    headers: {
      type: Array,
      default() {
        return [];
      }
    },
    bulkModifiers: {
      type: Array,
      default() {
        return [];
      }
    },
    showAddAdjustmentButton: {
      type: Boolean,
      default() {
        return false;
      }
    },
    adjustmentsChanged: {
      type: Boolean,
      default() {
        return false;
      }
    },
    adjustmentPanelProperties: {
      type: Object,
      default() {
        return {};
      }
    },
    overridesAvailable: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
    overrideBidAdjustments: {
      type: Boolean,
      default: () => {
        return false;
      }
    },
  },
  data() {
    return {
      bidAdjustmentsShowContents: true,
    };
  },
  methods: {
    overrideBidAdjustmentsToggle(val) {
      this.$emit('overrideBidAdjustmentsToggle', val);
    },
    sanitizeRow(row){
      if (row.comparer == 'Bulk') {
        row.compValue = null;
      }
      var newRow = JSON.parse(JSON.stringify(row));
      delete newRow.id;
      //delete newRow.showAddAdjustment;
      delete newRow.showAddAdjustmentButton;
      delete newRow.bulkListId;
      delete newRow.overrideTag;

      newRow.priority+=1;

      let tagText = tagIdToText(newRow.tag, availableTags, false);
      newRow.tag = tagText ? tagText : newRow.tag;

      if (newRow.comparer == 'Required') {
        newRow.compValue = null;
        newRow.operation = null;
        newRow.opValue = null;
        return newRow;
      }

      if (newRow.comparer == 'Not Set') {
        newRow.compValue = null;
      }

      let absCurrencyOpValue = Math.abs(newRow.opValue.replace(/[^\d\.\-]/g, '')).toFixed(CURRENCY_DECIMAL_DIGITS);

      if (row.comparer == 'Bulk') {
        newRow.opValue = 'Variable';
        const bulkModifierFound = this.bulkModifiers.find((value) => value.id == row.bulkListId);
        if (bulkModifierFound) {
          newRow.compValue = `${bulkModifierFound.name} (${bulkModifierFound.adjustmentsCount} items)`;
        }
      } else if (newRow.operation == 'Reject') {
        newRow.opValue = null;
        return newRow;
      } else if (newRow.operation == 'Subtract' || (parseFloat(newRow.opValue) < 0 && newRow.operation == 'Add')){
        newRow.operation = 'Subtract';
        newRow.opValue = '-$' + absCurrencyOpValue;
      } else if (newRow.operation == 'Add') {
        newRow.opValue = '$' + absCurrencyOpValue;
      } else if (newRow.operation == 'Multiply') {
        newRow.opValue = (parseFloat(newRow.opValue) < 0 ? '-' : '') + Math.abs(newRow.opValue).toFixed(MULTIPLIER_DECIMAL_DIGITS);
      } else if (newRow.operation == 'Override') {
        newRow.opValue = (parseFloat(newRow.opValue) < 0 ? '-$' : '$') + absCurrencyOpValue;
      } else if (newRow.operation == 'Override From Tag' || newRow.operation == 'OverrideFromTag') {
        newRow.operation = 'Override From Tag';
        newRow.opValue = row.overrideTag;
      }

      return newRow;
    },
    onShowRowAdjustment(row) {
      this.$emit('onShowRowAdjustment', row);
      this.$emit('onAddAdjustmentButtonClick', !this.showAddAdjustmentButton);
      this.$emit('onSetCurrentRow', row);
      this.$emit('onToggleEditRow', true);
      this.rowOptions = this.adjustmentPanelProperties,
      this.rowOptions.adjustment = {
        tagId: row.tag,
        comparisonTypeTemp: {value: row.comparer},
        comparisonValue: row.compValue,
        operationTypeTemp: {value: row.operation},
        operationValue: row.opValue,
        bulkListId: row.bulkListId,
        overrideTag: row.overrideTag,
      };
    },
    onOrderChange(unorderedRowsAndOldAndNewIndexes) {
      this.$emit('onOrderChange', unorderedRowsAndOldAndNewIndexes);
    },
    onDrag(){
      this.$emit('drag');
    },
    onSetCurrentRow(val) {
      this.$emit('onSetCurrentRow', val);
    },
    onToggleEditRow(val) {
      this.$emit('onToggleEditRow', val);
    },
    onBidAdjustmentsShowContentsClick() {
      this.bidAdjustmentsShowContents = !this.bidAdjustmentsShowContents;
    },
    onAddAdjustmentButtonClick(val) {
      this.$emit('onAddAdjustmentButtonClick', val);
    },
    onAddAdjustment(newAdjustment) {
      this.$emit('onAddAdjustment', newAdjustment);
    },
    onEditAdjustment(editedAdjustment) {
      this.$emit('onEditAdjustment', editedAdjustment);
    },
    onResetAdjustments() {
      this.$emit('onResetAdjustments');
    },
    onSaveAdjustments() {
      this.$emit('onSaveAdjustments');
    },
    onCancelAdjustments() {
      this.$emit('onCancelAdjustments');
    },
    onRemoveRow(row) {
      this.$emit('onRemoveRow', row);
    },
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>

<style>

</style>